/* External */
import {
  memo, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';

/* Ott-common */
import { createGetPlayerSchedules, createSchedulesCache, Errors } from '@dtvgo/player-utils';
import { useGetAndSet } from '@dtvgo/react-utils';
import { LiveInfoProvider, useLivePlayerSchedules } from '@dtvgo/player-utils-react';

/* Components */
import { VrioPlayer } from '~/components/Player';
import NpawMonitoringContainer from '~/components/Player/NpawMonitoring.container';
import PlayerMessage from '~/components/Player/PlayerMessage';

/* Hooks */
import useChannel from '~/hooks/useChannel';
import useQuery from '~/hooks/useQuery';
import { useOnBackground, useOnForeground } from '~/providers/ForegroundAppProvider';
import useHandlePlayerErrorWithReloadOnConnected from '~/components/Player/utils/useHandlePlayerErrorWithReloadOnConnected';
import useParentalControlError from '~/components/Player/utils/useParentalControlError';
import useNpawError from '~/hooks/useNpawError';

/* Utils */
import { goBack, redirectTo } from '~/utils/history';
import { EPG_FILTERS_ID } from '~/utils/constants';
import { getPath, Routes, getLink } from '~/utils/router';
import { useOnKeyDown, useOnBackPress } from '~/utils/keyDownEvents';
import platform from '~/utils/platform';
import { PLAYER_FAKE_SCHEDULE_DEFAULT_DURATION_MINUTES, PLAYER_FETCH_SCHEDULES_RETRY_COUNT, PLAYER_FETCH_SCHEDULES_RETRY_SECONDS } from '~/utils/env';
import backendService from '~/services/backendService';

const STOP_BUTTONS = [platform.keyCodes.mediaControl.stop];
const CACHE_SIZE = 4;
const IS_DELAYED_WINDOW_SECONDS = 10;
const getPlayerSchedules = createGetPlayerSchedules(backendService, {
  pageSize: CACHE_SIZE,
  maxAttempts: PLAYER_FETCH_SCHEDULES_RETRY_COUNT + 1,
  delayBetweenAttempts: PLAYER_FETCH_SCHEDULES_RETRY_SECONDS * 1000,
});
const schedulesCache = createSchedulesCache(getPlayerSchedules);

function LivePlayer() {
  const {
    state: {
      componentType,
      redirectFrom,
      lastContentUrl,
    } = {},
  } = useLocation();
  const history = useHistory();

  const { channelId } = useParams();
  const { origin } = useQuery();

  const [authorizerResponse, setAuthorizerResponse] = useState({});

  const channel = useChannel(channelId);
  const [
    { schedules: { current: schedule }, isLoading },
    { handleProgress: onProgress, updateState },
  ] = useLivePlayerSchedules(
    channelId,
    schedulesCache,
    PLAYER_FAKE_SCHEDULE_DEFAULT_DURATION_MINUTES,
    IS_DELAYED_WINDOW_SECONDS,
  );
  const playerRef = useRef();

  const {
    error, handleError, reportableError, handleReportableError,
  } = useHandlePlayerErrorWithReloadOnConnected();

  const { fromInterstitial, homeOnBack, interstitialContentId } = useQuery();
  const shouldGoToHomeOnBack = !!fromInterstitial || !!homeOnBack;

  const handleGoLive = useCallback(() => {
    updateState({ currentTimestamp: Date.now(), useNowNext: true, hardUpdate: true });
  }, [updateState]);

  const exitPlayer = useCallback(() => {
    if (shouldGoToHomeOnBack) {
      redirectTo(getPath(Routes.Private));
    } else {
      goBack();
    }
  }, [shouldGoToHomeOnBack]);

  // When the app goes to background, exits the player to avoid unexpected errors
  useOnBackground(useCallback(() => {
    playerRef.current?.stop();
    exitPlayer();
  }, [exitPlayer]));

  useOnForeground(useCallback(async () => {
    await updateState({ currentTimestamp: Date.now(), useNowNext: true, hardUpdate: true });
    exitPlayer();
  }, [exitPlayer, updateState]));

  useOnKeyDown({ listener: exitPlayer, forKeyCodes: STOP_BUTTONS });
  useOnBackPress({ listener: exitPlayer });

  const isCurrentInterstitialSchedule = interstitialContentId === schedule?.content;

  const handleProgress = useCallback((timeShift) => {
    const currentTime = Date.now();
    const delayInMilliseconds = Math.abs(timeShift) * 1000;
    const playerTime = currentTime - delayInMilliseconds;
    onProgress(playerTime, currentTime);
  }, [onProgress]);

  const scheduleTimes = useMemo(() => ({
    startTime: schedule?.live.startTime,
    endTime: schedule?.live.endTime,
  }), [schedule?.live.endTime, schedule?.live.startTime]);

  const channelName = channel?.title || schedule?.live.channelName;

  // TODO: Player should not render PlayerUI, it should be here
  const childrenWrapper = useCallback((children) => (
    <LiveInfoProvider schedule={scheduleTimes}>
      {children}
    </LiveInfoProvider>
  ), [scheduleTimes]);

  const [readyToPlay, setReadyToPlay] = useState(false);
  const [playbackStarted, setPlaybackStarted] = useGetAndSet(false);

  const npawError = useNpawError(reportableError);

  const handlePlaying = useCallback(() => {
    setPlaybackStarted(true);
  }, [setPlaybackStarted]);

  /* When channel is changed reset playbackStarted status */
  useEffect(() => {
    setPlaybackStarted(false);
  }, [channelId, setPlaybackStarted]);

  /* Handle schedule with blackout */
  useEffect(() => {
    if (schedule?.live.blackout) {
      handleReportableError(Errors.getBlackoutError(schedule));
    }
  }, [schedule, handleReportableError]);

  useParentalControlError(schedule, {
    onError: handleReportableError,
    playbackStarted,
    readyToPlay,
  });

  if (error) {
    // TODO: This should be handled by ErrorScreen, too
    if (error.category === Errors.ErrorCategory.PARENTAL) {
      if (error.data?.playbackStarted) {
        history.replace(
          getLink(Routes.Private.Player.Error),
          {
            redirectFrom: 'liveContent',
            channelIdParentalBlock: channelId,
            parentalControl: true,
            content: schedule,
          },
        );
      } else if (redirectFrom === 'parentalBlock') {
        sessionStorage.setItem('showToastParentalBlock', true);
        sessionStorage.setItem('parentalBlockedChannelId', channelId);
        sessionStorage.setItem('selectedFilterEpg', JSON.stringify(EPG_FILTERS_ID.ALL_STATIONS));
        history.push(
          getLink(Routes.Private.Live),
          {
            toastParentalBlock: true,
          },
        );
      } else if (componentType === 'miniepg') {
        history.replace(
          getLink(Routes.Private.Player.Error),
          {
            redirectFrom: 'miniEPG',
            lastContent: lastContentUrl,
            channelIdParentalBlock: channelId,
            parentalControl: true,
            content: schedule,
          },
        );
      } else {
        history.replace(
          getLink(Routes.Private.Player.Error),
          {
            parentalControl: true,
            content: schedule,
          },
        );
      }
    } else {
      return (
        <PlayerMessage
          content={schedule}
          error={error}
          contentTitle={channelName}
          channelId={channelId}
          exit={exitPlayer}
          isFromInterstitial={fromInterstitial}
          isCurrentInterstitialSchedule={isCurrentInterstitialSchedule}
        />
      );
    }
  }

  return (
    <VrioPlayer
      playbackId={channelId}
      channelId={channelId}
      schedule={schedule?.live}
      content={schedule}
      isContentRequestCompleted={!!schedule && !isLoading}
      channelName={channelName}
      isLive
      playSource={origin}
      isFromInterstitial={fromInterstitial}
      homeOnBack={shouldGoToHomeOnBack}
      isCurrentInterstitialSchedule={isCurrentInterstitialSchedule}
      onProgress={handleProgress}
      onError={handleError}
      onPlaying={handlePlaying}
      onAuthorizerError={handleReportableError}
      childrenWrapper={childrenWrapper}
      setPlayerReadyToPlay={setReadyToPlay}
      onAuthorizerResponseReceived={setAuthorizerResponse}
      onGoLive={handleGoLive}
      ref={playerRef}
    >
      <NpawMonitoringContainer
        isLive
        content={schedule}
        channelName={channelName}
        channelId={authorizerResponse?.channelId || channelId}
        playSource={origin}
        error={npawError}
        provider={authorizerResponse?.provider}
      />
    </VrioPlayer>
  );
}

export default memo(LivePlayer);
